import { FormEvent, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useNavigation } from "contexts/notificationsContext";
import { definirSenha, mensagemDeErro, obterDadosNovaSenha } from "services/auth";
import { AvisoErro, CartaoAcesso, RegraSenhaVisivel } from "./components/CartaoAcesso";
import { senhaAtendeRegra } from "./regraSenha";

interface NovaSenhaProps {
  /** /meuPrimeiroAcesso/:pId — mesma página, só muda o título. */
  primeiroAcesso?: boolean;
}

type EstadoLink = "carregando" | "valido" | "invalido";

/**
 * Nova senha e primeiro acesso (plano de 07/10/2026, B2). O e-mail de "Esqueci minha senha" e o de
 * "Enviar dados de acesso" (Gerência > Usuários) trazem o link /novaSenha/{IdPublico}, válido no dia
 * do pedido e uma única vez.
 */
export function NovaSenha({ primeiroAcesso = false }: NovaSenhaProps) {
  const { pId } = useParams();
  const navigate = useNavigate();
  const { openNotification } = useNavigation();

  const [estado, setEstado] = useState<EstadoLink>("carregando");
  const [dados, setDados] = useState<{ nome: string; email: string } | null>(null);
  const [senha, setSenha] = useState("");
  const [confirmacao, setConfirmacao] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [gravando, setGravando] = useState(false);

  useEffect(() => {
    let ativo = true;

    if (!pId) {
      setEstado("invalido");
      return;
    }

    obterDadosNovaSenha(pId)
      .then((resposta) => {
        if (!ativo) return;
        setDados(resposta);
        setEstado("valido");
      })
      .catch(() => {
        if (ativo) setEstado("invalido");
      });

    return () => {
      ativo = false;
    };
  }, [pId]);

  const handleSubmit = async (evento: FormEvent) => {
    evento.preventDefault();
    setErro(null);

    if (!senhaAtendeRegra(senha)) {
      setErro("A senha não atende à regra abaixo.");
      return;
    }
    if (senha !== confirmacao) {
      setErro("A confirmação não confere com a senha.");
      return;
    }

    setGravando(true);
    try {
      await definirSenha(pId!, senha);
      openNotification({ message: "Senha definida. Entre com a nova senha.", type: "success" });
      navigate("/login", { replace: true });
    } catch (e: any) {
      setErro(mensagemDeErro(e, "Não foi possível definir a senha. Tente novamente."));
      setGravando(false);
    }
  };

  const titulo = primeiroAcesso ? "Crie sua senha" : "Nova senha";

  return (
    <CartaoAcesso
      titulo={titulo}
      rodape={
        <Link to="/login" className="link-secondary">
          Voltar para o login
        </Link>
      }
    >
      {estado === "carregando" && <p className="text-center text-muted">Verificando o link...</p>}

      {estado === "invalido" && (
        <>
          <div className="alert alert-warning" role="alert">
            Link expirado ou já utilizado.
          </div>
          <Link to="/esqueciSenha" className="btn btn-mof w-100">
            SOLICITAR NOVO LINK
          </Link>
        </>
      )}

      {estado === "valido" && dados && (
        <form onSubmit={handleSubmit} noValidate>
          <p className="mb-3">
            Olá, <strong>{dados.nome}</strong>
            <br />
            <span className="text-muted">{dados.email}</span>
          </p>
          <AvisoErro mensagem={erro} />
          <div className="mb-3">
            <label className="form-label" htmlFor="nova-senha">
              Nova senha
            </label>
            <input
              id="nova-senha"
              type="password"
              className="form-control"
              autoComplete="new-password"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              autoFocus
            />
          </div>
          <RegraSenhaVisivel senha={senha} />
          <div className="mb-3">
            <label className="form-label" htmlFor="nova-senha-confirmacao">
              Confirme a nova senha
            </label>
            <input
              id="nova-senha-confirmacao"
              type="password"
              className="form-control"
              autoComplete="new-password"
              value={confirmacao}
              onChange={(e) => setConfirmacao(e.target.value)}
            />
          </div>
          <button type="submit" className="btn btn-mof w-100" disabled={gravando}>
            {gravando ? "SALVANDO..." : "SALVAR SENHA"}
          </button>
        </form>
      )}
    </CartaoAcesso>
  );
}
